// views/case-detail/shared.js — shared helpers + sub-components used across the
// case-detail tabs (extracted verbatim from views/CaseDetail.js).
import React, { useEffect, useRef, useState } from "react";
// Sprint 56b — every SlidePanel callsite is now a CenteredModal. Same
// chrome (frosted glass, blur backdrop), centered viewport.
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import Confirm from "../../components/Confirm";
import Modal from "../../components/Modal";
import { TipoPill, EstadoPill, SlaPill, TipoFalloPill, ESTADO_LABEL, PREFER_SUB_TIPO_STATES } from "../../components/Pills";
import { formatTipo } from "../../utils/case_taxonomy";
import { cases, insumos as insumosApi, users as usersApi, moderation as moderationApi } from "../../api/endpoints";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import InsumosTab from "../insumos/InsumosTab";
import LitisoftFields from "../../components/LitisoftFields";
import LitisoftIncompleteModal from "../../components/LitisoftIncompleteModal";
import LitisoftMandatoryInline from "../../components/LitisoftMandatoryInline";
import { TERMINO_OPTIONS, formatTermino } from "../../utils/termino";
import {
  canMarkContestadaDirectly,
  canRequestModeration,
  availableContestadaCategorias,
  CONTESTADA_LABELS as CONTESTADA_LABELS_FULL,
} from "../../utils/permissions";
import LawyerActionPanel from "../../components/lawyer/LawyerActionPanel";
// feature/carpetas-proceso — Phase 1. Dedup banner at the top of every
// case detail view when the proceso already has a carpeta billed.
import ProcesoCarpetasBanner from "../../components/ProcesoCarpetasBanner";
// feature/carpetas-phase2-docs — P2. Per-case carpeta documents panel.
import CarpetaDocumentsPanel from "../../components/CarpetaDocumentsPanel";
// feature/carpeta-lawyer-visibility — P2.2 change 5. Compact status
// pill shown to viewers WITHOUT carpeta upload rights.
import CarpetaStatusBadge from "../../components/CarpetaStatusBadge";
// feature/single-role-audit — multi-role helper.
import { rolesOf } from "../../utils/permissions";
// feature/inline-accionante-edit — case-detail surface for the
// existing PATCH /api/personas/:id endpoint.
import InlineEditAccionante from "../../components/InlineEditAccionante";
// feature/tipo-fallo-edit — modal for changing tipo_fallo on fallos.
import TipoFalloEditModal from "../../components/TipoFalloEditModal";
import { PERSONA_EDIT_ROLES } from "../Personas";


const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";

// feature/inline-edit — generic inline editor for case fields. Click pencil → input/textarea/select
// appears with Guardar/Cancelar. Caller's onSave receives the new value and is expected
// to throw on backend errors so the row stays in edit mode for retry.
function InlineEdit({ value, onSave, type = "text", options, placeholder, renderValue }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  const [busy, setBusy] = useState(false);
  if (!editing) {
    const display = renderValue ? renderValue() : (value || <span className="muted">{placeholder || "—"}</span>);
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span>{display}</span>
        <button className="ghost sm" style={{ padding: "0 6px", height: 22 }}
                onClick={() => { setV(value || ""); setEditing(true); }}
                title="Editar" aria-label="Editar">✎</button>
      </span>
    );
  }
  async function save() {
    setBusy(true);
    try { await onSave(v); setEditing(false); }
    catch { /* keep edit open on error */ }
    finally { setBusy(false); }
  }
  return (
    <div style={{ display: "flex", flexDirection: type === "textarea" ? "column" : "row", gap: 6, alignItems: type === "textarea" ? "stretch" : "center" }}>
      {type === "textarea" ? (
        <textarea value={v} onChange={(e) => setV(e.target.value)} disabled={busy} autoFocus />
      ) : type === "select" ? (
        <select value={v} onChange={(e) => setV(e.target.value)} disabled={busy} autoFocus>
          {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input value={v} onChange={(e) => setV(e.target.value)} disabled={busy} autoFocus />
      )}
      <div style={{ display: "flex", gap: 4 }}>
        <button className="sm" onClick={save} disabled={busy}>Guardar</button>
        <button className="ghost sm" onClick={() => setEditing(false)} disabled={busy}>Cancelar</button>
      </div>
    </div>
  );
}

const TABS = [
  { id: "general",   label: "General" },
  { id: "decision",  label: "Decisión",  decisionOnly: true },
  { id: "insumos",   label: "Insumos" },
  { id: "documentos",label: "Documentos" },
  { id: "historial", label: "Historial" },
  { id: "auditoria", label: "Auditoría", auditoriaOnly: true },
];

// feature/lawyer-agency-over-insumos.
// Mirrors the backend ['admin','directora','reparto','abogado','revisor_fallos']
// check. Backend is the load-bearing gate; this just hides the affordance
// from roles that wouldn't be allowed to use it anyway.
//
// Sprint 38b — defensive: if the `user` prop is empty/malformed at the moment
// React calls this (early render before AuthContext bootstraps, or a stale
// snapshot), fall back to reading the cached user from localStorage so we
// don't flash the "Solo el abogado..." message to a perfectly authorized
// reparto user just because the prop wasn't ready yet.
const LAWYER_ROLES = ["admin", "directora", "reparto", "abogado", "revisor_fallos"];

function hasLawyerRole(user) {
  let roles = (user?.roles && user.roles.length) ? user.roles
            : (user?.role ? [user.role] : []);
  if (!roles.length && typeof window !== "undefined") {
    try {
      const cached = JSON.parse(window.localStorage.getItem("eds.user") || "null");
      if (cached?.roles?.length) roles = cached.roles;
      else if (cached?.role)     roles = [cached.role];
    } catch (_) { /* ignore */ }
  }
  return LAWYER_ROLES.some((r) => roles.includes(r));
}

const INSUMOS_FLOW_ESTADOS = new Set([
  "pendiente_insumos", "insumos_solicitados", "expediente_solicitado", "insumos_recibidos",
]);

// feature/marcar-contestada.
// Specialist lawyers (sanciones / impugnaciones / memoriales tags) can mark
// directly; standard tags (tutelas / desacatos / fallos_contestacion) must
// request moderation instead. The badge always renders if c.contestada_at.
const CONTESTADA_LABELS = CONTESTADA_LABELS_FULL;

// feature/abogado-contestada. Exported so the abogado-side
// detail view (AbogadoCaseDetail.js → TrabajoColumn) can render the same
// gated affordance Molly + the other privileged-spec lawyers expect to
// see. Internal gating (canMarkContestadaDirectly + estado/contestada
// guards) means this is safe to render unconditionally — it returns null
// when the user isn't allowed.
function MarcarContestada({ c, user, onChanged }) {
  const toast = useToast();
  const [open, setOpen]           = useState(false);
  const [busy, setBusy]           = useState(false);
  const [categoria, setCategoria] = useState("");
  const [notas, setNotas]         = useState("");
  // sprint83 — Litisoft gate; backend returns 400 with missing_fields.
  const [litisoftMissing, setLitisoftMissing] = useState(null);

  // Already contestada → render the badge instead of the button.
  if (c.contestada_at) {
    return (
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8, marginTop: 12,
        padding: "8px 12px",
        background: "var(--accent-mint-bg)",
        color: "var(--accent-mint)",
        borderRadius: 4, fontSize: 13, fontWeight: 500,
      }}>
        <span>✓ Contestada el {new Date(c.contestada_at).toLocaleString("es-CO")}</span>
        {c.contestada_categoria && (
          <span style={{
            background: "var(--accent-mint)", color: "var(--on-accent)",
            padding: "2px 8px", borderRadius: 10, fontSize: 11,
          }}>{CONTESTADA_LABELS[c.contestada_categoria] || c.contestada_categoria}</span>
        )}
      </div>
    );
  }

  if (c.estado === "cerrado") return null;
  if (!canMarkContestadaDirectly(user, c)) return null;

  const cats = availableContestadaCategorias(user);

  async function submit() {
    if (!categoria) return;
    setBusy(true);
    try {
      await cases.marcarContestada(c.id, { categoria, notas: notas.trim() || null });
      toast.push("Caso marcado como contestada");
      setOpen(false); setCategoria(""); setNotas("");
      onChanged?.();
    } catch (e) {
      const data = e?.response?.data;
      if (data?.error === "litisoft_incomplete" && Array.isArray(data.missing_fields)) {
        setLitisoftMissing(data.missing_fields);
      } else {
        toast.push(data?.error || "Error al marcar contestada", "error");
      }
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <button onClick={() => setOpen(true)} disabled={busy}>
        {busy && <Spinner color="currentColor" />} Marcar como contestada
      </button>
      {/* Sprint 55d — gate loosened so any assigned lawyer can mark
          contestada. Helper copy now reflects the broader UX: mark when
          you've actually responded; use moderación if blocked. */}
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        Marque el caso como contestado cuando haya radicado la respuesta.
        Si los insumos están demorados o son deficientes y necesita extensión
        de plazo, use "Solicitar revisión de moderación".
      </div>
      {open && (
        <Modal
          open={open}
          title="Marcar caso como contestada"
          onClose={busy ? undefined : () => setOpen(false)}
          footer={<>
            <button className="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancelar</button>
            <button disabled={busy || !categoria} onClick={submit}>
              {busy && <Spinner color="currentColor" />} Confirmar
            </button>
          </>}
        >
          <p style={{ marginTop: 0, fontSize: 13 }}>
            Va a marcar el caso <strong>{c.case_number}</strong> como contestado. Esto cierra
            el caso y libera al equipo de insumos si estaban trabajando en él.
          </p>
          <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
            Categoría <span style={{ color: "var(--color-warning)" }}>*</span>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)}
                    disabled={busy} required autoFocus
                    style={{ marginTop: 4, width: "100%" }}>
              <option value="">— Seleccionar —</option>
              {cats.map((cat) => (
                <option key={cat} value={cat}>{CONTESTADA_LABELS[cat] || cat}</option>
              ))}
            </select>
          </label>
          <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
            Notas (opcional, máx 500 caracteres)
            <textarea value={notas} onChange={(e) => setNotas(e.target.value)}
                      maxLength={500} rows={3} disabled={busy}
                      placeholder="Detalles adicionales sobre la respuesta…"
                      style={{ marginTop: 4, width: "100%" }} />
          </label>
        </Modal>
      )}
      <LitisoftIncompleteModal
        open={!!litisoftMissing}
        missingFields={litisoftMissing || []}
        onClose={() => setLitisoftMissing(null)}
        onGoToPanel={() => { setLitisoftMissing(null); setOpen(false); }}
      />
    </div>
  );
}

// feature/case-flags. Toggle the derecho_peticion +
// medida_provisional flags inline. Visible to triage roles only.
function CaseFlagsToggles({ c, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  async function setFlag(field, value) {
    setBusy(true);
    try {
      await cases.setFlags(c.id, { [field]: value });
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error", "error");
    } finally { setBusy(false); }
  }

  return (
    <div className="col" style={{ gap: 6 }}>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
        <input type="checkbox" disabled={busy}
               checked={!!c.flag_derecho_peticion}
               onChange={(e) => setFlag("flag_derecho_peticion", e.target.checked)} />
        Derecho de petición
      </label>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
        <input type="checkbox" disabled={busy}
               checked={!!c.flag_medida_provisional}
               onChange={(e) => setFlag("flag_medida_provisional", e.target.checked)} />
        Medida provisional
      </label>
      {/* feature/nulidad-bandera — sprint86. */}
      <label title="Marca esta opción cuando la tutela llega nuevamente después de que se decretó nulidad sobre la radicación anterior."
             style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 0, textTransform: "none", letterSpacing: 0, fontSize: 13, color: "var(--text-body)" }}>
        <input type="checkbox" disabled={busy}
               checked={!!c.flag_vuelve_despues_de_nulidad}
               onChange={(e) => setFlag("flag_vuelve_despues_de_nulidad", e.target.checked)} />
        Vuelve después de nulidad
      </label>
    </div>
  );
}

// feature/sancion-lifecycle, expanded in Sprint 52.
// Fiorella's 9-state machine, matching her WhatsApp wording exactly.
// Loop: nulidad_reinicia → emitida (restart trámite). Super-alerts on
// inaplicacion_archivo and obedece_confirma.
export const SANCION_STATE_LABELS = {
  asignado_abogado:                 "Recién asignada",
  sancion_emitida:                  "Sanción emitida",
  sancion_en_consulta:              "En consulta",
  sancion_revoca_en_consulta:       "Revoca en consulta",
  sancion_confirma_en_consulta:     "Confirma en consulta",
  sancion_nulidad_reinicia:         "Nulidad / reinicia trámite",
  sancion_obedece_revoca:           "Obedece al superior — revoca la sanción",
  sancion_obedece_confirma:         "Obedece al superior — confirma la sanción",
  sancion_archivo:                  "Archivo",
  sancion_inaplicacion_archivo:     "Inaplicación / archivo",
  cerrado:                          "Cerrado",
};

const SANCION_TRANSITIONS = {
  asignado_abogado: [
    { to: "sancion_emitida", label: "Marcar sanción emitida" },
  ],
  sancion_emitida: [
    { to: "sancion_en_consulta", label: "Enviar a consulta" },
    { to: "sancion_archivo",     label: "Archivar (sin consulta)", secondary: true },
  ],
  sancion_en_consulta: [
    { to: "sancion_revoca_en_consulta",   label: "Superior revocó" },
    { to: "sancion_confirma_en_consulta", label: "Superior confirmó" },
    { to: "sancion_nulidad_reinicia",     label: "Nulidad / reinicia trámite", warning: true },
  ],
  sancion_revoca_en_consulta: [
    { to: "sancion_obedece_revoca", label: "Obedece al superior — revoca la sanción" },
  ],
  sancion_confirma_en_consulta: [
    { to: "sancion_obedece_confirma",     label: "Obedece al superior — confirma la sanción" },
    { to: "sancion_inaplicacion_archivo", label: "⚠️ Solicitar inaplicación", danger: true },
  ],
  sancion_nulidad_reinicia: [
    { to: "sancion_emitida",  label: "Reiniciar trámite (nueva sanción emitida)" },
    { to: "sancion_archivo",  label: "Archivar (no se reinicia)", secondary: true },
  ],
  sancion_obedece_revoca: [
    { to: "sancion_archivo", label: "Archivar" },
  ],
  sancion_obedece_confirma: [
    { to: "sancion_archivo",              label: "Archivar" },
    { to: "sancion_inaplicacion_archivo", label: "⚠️ Solicitar inaplicación", danger: true },
  ],
  sancion_archivo: [
    { to: "cerrado", label: "Cerrar caso", secondary: true },
  ],
  sancion_inaplicacion_archivo: [
    { to: "cerrado", label: "Cerrar caso", secondary: true },
  ],
};

function hasSancionesSpecialization(user) {
  const specs = user?.specializations || [];
  return specs.includes("sanciones");
}

function SancionLifecycle({ c, user, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  if (c.tipo !== "sancion") return null;
  const userRoles = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const isPriv = userRoles.includes("admin") || userRoles.includes("directora");
  const isAssignedSancionista = userRoles.includes("abogado")
                              && c.abogado_asignado_id === user?.id
                              && hasSancionesSpecialization(user);
  if (!isPriv && !isAssignedSancionista) return null;
  const opts = SANCION_TRANSITIONS[c.estado];

  async function transition(to) {
    setBusy(true);
    try {
      await cases.sancionTransition(c.id, to);
      toast.push("Estado actualizado.");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al cambiar estado", "error");
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
        Etapa procesal de la sanción
      </div>
      <div style={{ fontSize: 13, marginBottom: 8 }}>
        Estado actual: <strong>{SANCION_STATE_LABELS[c.estado] || c.estado}</strong>
      </div>
      {opts && opts.length > 0 ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {opts.map((o) => {
            let style;
            let className = "sm";
            if (o.danger) {
              style = { background: "var(--color-warning)", color: "var(--on-accent)", borderColor: "var(--color-warning)" };
            } else if (o.warning) {
              style = { background: "#fbbf24", color: "#451a03", borderColor: "#fbbf24" };
            } else if (o.secondary) {
              className = "secondary sm";
            } else {
              className = "secondary sm";
            }
            return (
              <button key={o.to} className={className} style={style}
                      disabled={busy} onClick={() => transition(o.to)}>
                {busy && <Spinner color={o.danger ? "#fff" : undefined} />} {o.label}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="muted" style={{ fontSize: 12 }}>Sin transiciones disponibles desde este estado.</div>
      )}
      {c.estado === "sancion_inaplicacion_archivo" && (
        <div className="warn-inline" style={{ marginTop: 8, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <span style={{ fontSize: 18 }}>⚠</span>
          <div>
            <strong>Sanción en INAPLICACIÓN — archivo</strong>
            <div style={{ fontSize: 12, marginTop: 2 }}>
              Etapa más grave del proceso. Notificada a dirección y administración.
            </div>
          </div>
        </div>
      )}
      {c.estado === "sancion_obedece_confirma" && (
        <div style={{
          marginTop: 8, display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px",
          background: "var(--color-warning-bg)",
          borderLeft: "3px solid var(--color-warning)",
          borderRadius: 4, fontSize: 13, color: "var(--color-warning-ink)",
        }}>
          <span style={{ fontSize: 18 }}>⚠</span>
          <strong>Sanción CONFIRMADA — la inferior obedeció al superior.</strong>
        </div>
      )}
    </div>
  );
}

// feature/insumo-deficiente. Mark the insumo as deficient
// (already arrived in earlier instance, incomplete, etc). Clears
// requiere_insumo so the case advances. Available to admin/directora,
// insumos team, and the assigned lawyer.
function MarcarInsumoDeficiente({ c, user, onChanged }) {
  const toast = useToast();
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [reason, setReason] = useState("");

  if (c.insumo_marcado_deficiente_at || !c.requiere_insumo) return null;
  const roles = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const allowed = ["admin", "directora", "insumos"].some((r) => roles.includes(r))
                || (roles.includes("abogado") && c.abogado_asignado_id === user?.id);
  if (!allowed) return null;

  const ok = reason.trim().length >= 10;

  async function submit() {
    if (!ok) return;
    setBusy(true);
    try {
      await cases.marcarInsumoDeficiente(c.id, reason.trim());
      toast.push("Insumo marcado como deficiente.");
      setOpen(false); setReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al marcar insumo deficiente", "error");
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <button className="secondary sm" onClick={() => setOpen(true)}>
        Marcar insumo deficiente / ya existía
      </button>
      {open && (
        <Modal
          open={open}
          title="Marcar insumo deficiente"
          onClose={busy ? undefined : () => setOpen(false)}
          footer={<>
            <button className="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancelar</button>
            <button disabled={busy || !ok} onClick={submit}>
              {busy && <Spinner color="currentColor" />} Confirmar
            </button>
          </>}
        >
          <p style={{ marginTop: 0, fontSize: 13 }}>
            El caso <strong>{c.case_number}</strong> dejará de requerir insumos. Útil
            cuando el insumo ya existía (ej: instancia anterior) o llegó incompleto.
          </p>
          <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
            Razón (mínimo 10 caracteres) <span style={{ color: "var(--color-warning)" }}>*</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      maxLength={500} rows={3} disabled={busy} autoFocus
                      placeholder="Ej: insumo ya estaba en la instancia anterior, archivo deficiente…"
                      style={{ marginTop: 4, width: "100%" }} />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {reason.trim().length} / 10 caracteres mínimos
            </div>
          </label>
        </Modal>
      )}
    </div>
  );
}

// feature/moderation-escalation.
// For lawyers without a privileged specialization tag (tutelas / desacatos /
// fallos_contestacion), the escalation route when blocked by missing or
// deficient insumos is to ask moderation (Natali / César + admin/directora).
// They cannot bypass directly to cerrado.
// feature/abogado-contestada. Exported alongside MarcarContestada
// so standard-tag lawyers (tutelas / desacatos / fallos_contestacion) get
// the moderation escalation route on the abogado-side detail view too.
function SolicitarModeracion({ c, user, onChanged }) {
  const toast = useToast();
  const [open, setOpen]   = useState(false);
  const [busy, setBusy]   = useState(false);
  const [reason, setReason] = useState("");

  if (c.estado === "cerrado" || c.contestada_at) return null;
  // Sprint 55d — both buttons can render together. The lawyer picks based
  // on situation: "Marcar como contestada" when responded, "Solicitar
  // moderación" when blocked by missing/deficient insumos. The earlier
  // mutual exclusion (hide moderation when user could mark contestada)
  // assumed only privileged-spec lawyers had the contestada button — no
  // longer true now that any assigned lawyer does.
  if (!canRequestModeration(user, c)) return null;

  const ok = reason.trim().length >= 20;

  async function submit() {
    if (!ok) return;
    setBusy(true);
    try {
      await moderationApi.request(c.id, reason.trim());
      toast.push("Solicitud de moderación enviada.");
      setOpen(false); setReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al solicitar moderación", "error");
    } finally { setBusy(false); }
  }

  return (
    <div style={{ marginTop: 12 }}>
      <button className="secondary" onClick={() => setOpen(true)} disabled={busy}>
        Solicitar revisión de moderación
      </button>
      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        Si los insumos no han llegado a tiempo o están deficientes, moderación puede
        extender el plazo, marcar el insumo deficiente, o reasignar el caso.
      </div>
      {open && (
        <Modal
          open={open}
          title="Solicitar revisión de moderación"
          onClose={busy ? undefined : () => setOpen(false)}
          footer={<>
            <button className="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancelar</button>
            <button disabled={busy || !ok} onClick={submit}>
              {busy && <Spinner color="currentColor" />} Enviar solicitud
            </button>
          </>}
        >
          <p style={{ marginTop: 0, fontSize: 13 }}>
            Va a pedirle al equipo de moderación que revise el caso{" "}
            <strong>{c.case_number}</strong>. Las opciones que ellos pueden tomar:
          </p>
          <ul style={{ fontSize: 13, paddingLeft: 18 }}>
            <li>Extender el plazo del abogado</li>
            <li>Marcar el insumo como deficiente y dejar avanzar</li>
            <li>Reasignar el caso a otro abogado</li>
            <li>Confirmar la vencida (Tania la verá en reportes)</li>
          </ul>
          <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
            Razón (mínimo 20 caracteres) <span style={{ color: "var(--color-warning)" }}>*</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      maxLength={1000} rows={4} disabled={busy} autoFocus
                      placeholder="Explique por qué necesita moderación: insumo no llegó, llegó incompleto, etc."
                      style={{ marginTop: 4, width: "100%" }} />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              {reason.trim().length} / 20 caracteres mínimos
            </div>
          </label>
        </Modal>
      )}
    </div>
  );
}

function LawyerAgencyButtons({ c, user, onChanged }) {
  const toast = useToast();
  const [skipOpen, setSkipOpen] = useState(false);
  const [reqOpen, setReqOpen]   = useState(false);
  const [reason, setReason]     = useState("");
  const [busy, setBusy]         = useState(false);

  if (!hasLawyerRole(user)) {
    return <span className="muted" style={{ fontSize: 12 }}>
      Su rol no permite saltar/solicitar insumos. Roles autorizados: reparto, abogado, revisor de fallos, directora, admin.
    </span>;
  }

  const inFlow = INSUMOS_FLOW_ESTADOS.has(c.estado);
  // Outside the insumos flow + already cerrado → no longer actionable here.
  const canRequest = !inFlow && c.estado !== "cerrado";
  // Skip is only valid from inside the insumos flow.
  const canSkip = inFlow;

  if (!canSkip && !canRequest) {
    return <span className="muted" style={{ fontSize: 12 }}>
      Sin acciones para el estado actual ({ESTADO_LABEL[c.estado] || c.estado}). El caso ya pasó la fase de insumos o está cerrado.
    </span>;
  }

  async function doSkip() {
    setBusy(true);
    try {
      const r = await insumosApi.skipInsumos(c.id, reason.trim() || null);
      const next = r.data?.estado;
      toast.push(next === "cerrado"
        ? "Caso cerrado: no requiere insumos."
        : "Caso enviado a Molly para decisión de impugnación.");
      setSkipOpen(false); setReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al saltar insumos", "error");
    } finally { setBusy(false); }
  }
  async function doRequest() {
    setBusy(true);
    try {
      const r = await insumosApi.requestInsumos(c.id, reason.trim() || null);
      const assigned = r.data?.assigned_user_id;
      toast.push(assigned
        ? "Insumos solicitados y asignados correctamente."
        : "Insumos solicitados. La asignación queda pendiente — revísela en /admin/users.");
      setReqOpen(false); setReason("");
      onChanged?.();
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al solicitar insumos", "error");
    } finally { setBusy(false); }
  }

  return (
    <div style={{ display: "inline-flex", gap: 8, flexWrap: "wrap" }}>
      {canSkip && (
        <button className="secondary sm" onClick={() => { setReason(""); setSkipOpen(true); }}>
          Marcar "no requiere insumos"
        </button>
      )}
      {canRequest && (
        <button className="secondary sm" onClick={() => { setReason(""); setReqOpen(true); }}>
          Solicitar insumos
        </button>
      )}

      {skipOpen && (
        <Modal open={skipOpen} title='Marcar como "no requiere insumos"'
               onClose={busy ? undefined : () => setSkipOpen(false)}
               footer={<>
                 <button className="secondary" disabled={busy} onClick={() => setSkipOpen(false)}>Cancelar</button>
                 <button disabled={busy} onClick={doSkip}>{busy && <Spinner color="currentColor" />} Confirmar</button>
               </>}>
          <p style={{ marginTop: 0 }}>
            El caso <strong>{c.case_number}</strong> dejará la cola de insumos.
            Pasará a:
          </p>
          <div className="card tight" style={{ padding: 8, fontSize: 13 }}>
            {c.tipo === "fallo"
              ? <span><strong>Cola de revisión de impugnación</strong> (Molly decidirá si se impugna).</span>
              : <span><strong>Cerrado.</strong> No se generará solicitud de insumos.</span>}
          </div>
          <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
            Razón (opcional, máx 500 caracteres)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      maxLength={500} rows={3}
                      placeholder="Ej: caso simple, no requiere preparación adicional…"
                      style={{ marginTop: 4, width: "100%" }} />
          </label>
        </Modal>
      )}

      {reqOpen && (
        <Modal open={reqOpen} title="Solicitar insumos para este caso"
               onClose={busy ? undefined : () => setReqOpen(false)}
               footer={<>
                 <button className="secondary" disabled={busy} onClick={() => setReqOpen(false)}>Cancelar</button>
                 <button disabled={busy} onClick={doRequest}>{busy && <Spinner color="currentColor" />} Confirmar</button>
               </>}>
          <p style={{ marginTop: 0 }}>
            El caso <strong>{c.case_number}</strong> entrará en la cola de
            insumos y se asignará automáticamente (Natali o César).
          </p>
          <ul style={{ fontSize: 13, paddingLeft: 18, marginTop: 8 }}>
            <li>Estado pasa a <code>pendiente_insumos</code>.</li>
            <li>Asignación automática vía round-robin (cap diario respetado).</li>
            <li>Se notifica a directora y admin.</li>
          </ul>
          <label style={{ marginTop: 12, display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
            Razón (opcional, máx 500 caracteres)
            <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      maxLength={500} rows={3}
                      placeholder="Ej: requiere historia clínica completa…"
                      style={{ marginTop: 4, width: "100%" }} />
          </label>
        </Modal>
      )}
    </div>
  );
}

export {
  fmt, InlineEdit, hasLawyerRole, MarcarContestada, CaseFlagsToggles,
  hasSancionesSpecialization, SancionLifecycle, MarcarInsumoDeficiente,
  SolicitarModeracion, LawyerAgencyButtons, TABS, CONTESTADA_LABELS, TipoActuacionDisplay,
};

// feature/tipo-fallo-edit — display component for the "Tipo de
// actuación" dd. For fallo cases AND viewers in PERSONA_EDIT_ROLES,
// renders the formatted tipo string + an "Editar" button that
// opens TipoFalloEditModal. For other tipos OR viewers without the
// role, renders plain text.
function TipoActuacionDisplay({ c, onChanged }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const display = formatTipo(c.tipo, c.sub_tipo, c.no_aplica);
  const userRoles = (user?.roles && user.roles.length) ? user.roles : (user?.role ? [user.role] : []);
  const canEdit = c.tipo === "fallo"
    && PERSONA_EDIT_ROLES.some(r => userRoles.includes(r));

  if (!canEdit) {
    return <span>{display}</span>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <span>{display}</span>
      <button
        type="button"
        className="ghost sm"
        style={{ fontSize: 11, padding: "2px 8px" }}
        onClick={() => setOpen(true)}
        title="Editar tipo de actuación"
      >Editar</button>
      {open && (
        <TipoFalloEditModal
          open={open}
          c={c}
          onClose={() => setOpen(false)}
          onUpdated={onChanged}
        />
      )}
    </span>
  );
}
